import React, { useState } from 'react';
import { Divider, IconButton, ListItemText, Menu, MenuItem, SxProps, Theme } from '@mui/material';
import { MoreVertIcon } from 'src/icons/MoreVertIcon';
import { useLocation, useNavigate } from 'react-router';
import { ts } from "src/utils/translate";
import ProjectDeleteDialog from "src/components/ProjectMenu/dialogs/ProjectDeleteDialog";
import ProjectDuplicateDialog from "src/components/ProjectMenu/dialogs/ProjectDuplicateDialog";
import ProjectLeaveDialog from "src/components/ProjectMenu/dialogs/ProjectLeaveDialog";
import { DownloadSvgIcon } from "src/pages/ProjectsPage/icons/DownloadIcon";
import { useCloneProjectMutation, useDeleteProjectMutation, useLeaveProjectMutation } from 'src/api/v1/project-ApiSlice';
import { useLazyIsLoxoneAvailableQuery } from 'src/api/v2/project-v2-ApiSlice';
import { LoxoneConfigDownloadRequest } from "src/model/api/quote/loxone-config-download-request.model";
import { getFileDate } from "src/utils/controllerAdapterForOfferPage";
import { getAPIURL } from "src/utils/url";
import { useFileDownload } from "src/api/file-download-service";
import { ElectricalPlanGenerateRequest } from "src/model/api/quote/electrical-plan-generate-request.model";
import { hasPermissionForFuncInRole } from "src/utils/controllerHasPermissionsAdapter";
import Tooltip from "@mui/material/Tooltip";
import { useGetVersionQuery } from "src/api/app-ApiSlice";
import { theme } from 'src/theme/mui-theme';
import { alpha } from '@mui/material/styles';
import { GetProjectResponseProject } from "src/model/api/v2/data-contracts";
import { useAppDispatch } from "src/app/hooks";
import { showSnackBar } from "src/api/ui-slice";
import {
    DownloadGateState,
    getLoxoneDownloadDisabledReason,
    getOneWireDownloadDisabledReason,
    needProLicenceReason
} from "src/utils/downloadDisabledReason";

type Props = {
    extendedProject: GetProjectResponseProject;
    showOpenProject?: boolean;
    iconButtonSx?: SxProps<Theme>;
};

export const ProjectMenu = ({ extendedProject, showOpenProject = true, iconButtonSx }: Props) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);

    const [cloneProject] = useCloneProjectMutation();
    const [deleteProject] = useDeleteProjectMutation();
    const [leaveProject] = useLeaveProjectMutation();

    const { fetchData } = useFileDownload();
    const [triggerLoxoneCheck, { data: loxoneAvailability }] = useLazyIsLoxoneAvailableQuery();

    const {data: version} = useGetVersionQuery()

    const handleEdit = () => {
        navigate(`/projects/${extendedProject.project.id}`);
    };

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        triggerLoxoneCheck(extendedProject.project.id);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    function exportElectricalPlan() {
        const request: ElectricalPlanGenerateRequest = {
            project_id: extendedProject.project.id,
        };
        const date = getFileDate();

        const fileName = extendedProject.project.project_name + " " + date + ".pdf";
        fetchData(request, `${getAPIURL()}/v1/electrical-plan/generate`, fileName).then();
    }

    function exportLoxoneConfig() {
        const request: LoxoneConfigDownloadRequest = {
            project_id: extendedProject.project.id,
        };
        const date = getFileDate();

        const fileName = extendedProject.project.project_name + " " + date + ".Loxone";
        fetchData(request, `${getAPIURL()}/v1/loxone-config/download`, fileName).then();
    }

    const downloadGateState: DownloadGateState = {
        isPro: !!version?.is_pro,
        loxoneConfigPaid: !!extendedProject.paidFeature?.loxoneconfig_paid,
        stromlaufplanPaid: !!extendedProject.paidFeature?.stromlaufplan_paid,
        isLoxoneConfigDownloadAvailable: loxoneAvailability?.is_loxone_config_download_available
            ?? !!extendedProject.paidFeature?.is_loxone_config_download_available,
        hasPermission: (functionName) => hasPermissionForFuncInRole(functionName, extendedProject.userRole)
    };

    const loxonDownloadDisabled = getLoxoneDownloadDisabledReason(downloadGateState);
    const oneWireDownloadDisabled = getOneWireDownloadDisabledReason(downloadGateState);

    const handleDuplicateConfirm = () => {
        setDuplicateDialogOpen(false);
        cloneProject({ id: extendedProject.project.id })
            .unwrap()
            .then(() => {
                dispatch(showSnackBar({ text: ts('duplicate_project_success', 'Project duplicated successfully'), severity: 'success' }));
                navigate('/projects');
            })
            .catch(() => {
                dispatch(showSnackBar({ text: ts('duplicate_project_error', 'Failed to duplicate project'), severity: 'error' }));
            });
    };

    const handleDeleteConfirm = () => {
        setDeleteDialogOpen(false);
        deleteProject(extendedProject.project.id)
            .unwrap()
            .then(() => {
                dispatch(showSnackBar({ text: ts('delete_project_success', 'Project deleted successfully'), severity: 'success' }));
                navigate('/projects');
            })
            .catch(() => {
                dispatch(showSnackBar({ text: ts('delete_project_error', 'Failed to delete project'), severity: 'error' }));
            });
    };

    const handleLeaveConfirm = () => {
        setLeaveDialogOpen(false);
        leaveProject(extendedProject.project.id)
            .unwrap()
            .then(() => {
                dispatch(showSnackBar({ text: ts('leave_project_success', 'Project left successfully'), severity: 'success' }));
                navigate('/projects');
            })
            .catch(() => {
                dispatch(showSnackBar({ text: ts('leave_project_error', 'Failed to leave project'), severity: 'error' }));
            });
    };

    const menuItemSx = {
        color: '#000000',
        fontSize: '0.9rem',
        '&:hover': {
            color: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
        },
    };

    return (
        <>
            <IconButton
                aria-label="more"
                onClick={handleClick}
                sx={{ color: 'gray', ...iconButtonSx }}
            >
                <MoreVertIcon />
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '6px',
                            width: 320,
                        },
                    },
                    list: {
                        sx: {
                            pt: '10px',
                            pb: '10px',
                        },
                    },
                }}
            >
                {showOpenProject && (
                    <MenuItem
                        onClick={() => {
                            navigate(`/planner/${extendedProject.project.id}`);
                            handleClose();
                        }}
                        sx={menuItemSx}
                    >
                        {ts('project_list_menu_open', 'Open')}
                    </MenuItem>
                )}

                <MenuItem
                    onClick={() => {
                        handleEdit();
                        handleClose();
                    }}
                    sx={menuItemSx}
                >
                    {ts('project_list_menu_edit', 'Edit')}
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        navigate(`/projects/${extendedProject.project.id}/settings`, { state: { from: location.pathname } });
                        handleClose();
                    }}
                    sx={menuItemSx}
                >
                    {ts('project_list_menu_settings', 'Project Settings')}
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        if (version?.is_read_only_user) {
                            dispatch(showSnackBar({ text: needProLicenceReason().reason, severity: 'error' }));
                        } else {
                            navigate(`/projects/${extendedProject.project.id}/users`, { state: { from: location.pathname } });
                        }
                        handleClose();
                    }}
                    sx={menuItemSx}
                >
                    {ts('project_list_menu_user', 'Users')}
                </MenuItem>

                <Divider />

                <MenuItem
                    onClick={() => {
                        navigate(`/offer/${extendedProject.project.id}`);
                        handleClose();
                    }}
                    sx={menuItemSx}
                >
                    {ts('project_list_menu_offer', 'Offer')}
                </MenuItem>

                <Divider />

                <Tooltip title={oneWireDownloadDisabled.reason}>
                  <span>
                    <MenuItem
                        disabled={oneWireDownloadDisabled.disabled}
                        onClick={exportElectricalPlan}
                        sx={menuItemSx}
                    >
                      <ListItemText>
                        {ts('project_list_menu_electrical_cabinet_drawing', 'Electrical Cabinet Drawing')}
                      </ListItemText>
                      <DownloadSvgIcon sx={{ ml: 'auto' }} fontSize="small" />
                    </MenuItem>
                  </span>
                </Tooltip>

                <Tooltip title={loxonDownloadDisabled.reason}>
                  <span>
                    <MenuItem
                        disabled={loxonDownloadDisabled.disabled}
                        onClick={exportLoxoneConfig}
                        sx={menuItemSx}
                    >
                      <ListItemText>{ts('project_list_menu_loxone_config_export', 'Loxone Config Export')}</ListItemText>
                      <DownloadSvgIcon sx={{ ml: 'auto' }} fontSize="small" />
                    </MenuItem>
                  </span>
                </Tooltip>

                <Divider />

                <MenuItem
                    onClick={() => {
                        setDuplicateDialogOpen(true);
                        handleClose();
                    }}
                    sx={menuItemSx}
                >
                    {ts('project_list_menu_duplicate', 'Duplicate')}
                </MenuItem>

                {extendedProject.own ? (
                    <MenuItem
                        onClick={() => {
                            setDeleteDialogOpen(true);
                            handleClose();
                        }}
                        sx={menuItemSx}
                    >
                        {ts('p_tool_delete', 'Delete')}
                    </MenuItem>
                ) : (
                    <MenuItem
                        onClick={() => {
                            setLeaveDialogOpen(true);
                            handleClose();
                        }}
                        sx={menuItemSx}
                    >
                        {ts('leave_word', 'Leave')}
                    </MenuItem>
                )}
            </Menu>

            <ProjectDuplicateDialog
                open={duplicateDialogOpen}
                onClose={() => setDuplicateDialogOpen(false)}
                onConfirm={handleDuplicateConfirm}
                projectName={extendedProject.project.project_name}
            />

            <ProjectDeleteDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleDeleteConfirm}
            />

            <ProjectLeaveDialog
                open={leaveDialogOpen}
                onClose={() => setLeaveDialogOpen(false)}
                onConfirm={handleLeaveConfirm}
            />
        </>
    );
};
